<script setup lang="ts">
import { useFileTableContext } from '@/modules/Workspaces/KnowledgeHub/Files/context'
import FileTableRow from '@/modules/Workspaces/KnowledgeHub/Files/FileTableRow.vue'
import { provideHubRowContext } from '@/modules/Workspaces/KnowledgeHub/Hubs/FileTable/context'
import DataRowMenu from '@/modules/Workspaces/KnowledgeHub/Hubs/FileTable/DataRowMenu.vue'
import NameCell from '@/modules/Workspaces/KnowledgeHub/Hubs/FileTable/NameCell.vue'
import RowGrid from '@/modules/Workspaces/KnowledgeHub/Hubs/FileTable/RowGrid.vue'
import type { HubFile } from '@/modules/Workspaces/KnowledgeHub/Hubs/types'
import DotIndicator from '@/uiKit/DotIndicator.vue'
import ToolTip from '@/uiKit/ToolTip.vue'
import { useElementHover, useTimeAgo } from '@vueuse/core'
import { computed, ref, useTemplateRef } from 'vue'

const props = defineProps<{ item: HubFile }>()

const selection = useFileTableContext()
const isSelected = computed(() => selection.isSelected(props.item.entityId))

const isRenaming = ref(false)
const isMenuOpen = ref(false)

const row = useTemplateRef('row')
const isHovering = useElementHover(computed(() => row.value?.$el))

const uploadedStates: (typeof props.item.uploadStatus)[] = ['complete', 'computing']
const isUploading = computed(() => !uploadedStates.includes(props.item.uploadStatus))

provideHubRowContext({
  isRenaming,
  isHovering,
  item: computed(() => props.item),
  isUploading,
})
</script>

<template>
  <FileTableRow
    ref="row"
    :class="{ selected: isSelected, 'text-text-disabled': isUploading }"
    class="group"
  >
    <template #grid>
      <RowGrid
        class="rounded-lg"
        data-selection-boundary
        :class="[
          isSelected
            ? 'bg-background-selected group-hover:bg-background-selected-hovered'
            : 'group-hover:bg-background-transparent-hovered',
          isMenuOpen &&
            (isSelected ? 'bg-background-selected-hovered!' : 'bg-background-transparent-hovered!'),
        ]"
      >
        <template #filename>
          <NameCell data-tree-padded-cell />
        </template>

        <template #summary>
          <!-- Text in this column would most likely go all the way to the right, -->
          <!-- being too close to the text of next column, that's the reason for right padding -->
          <span class="truncate pr-4">
            <ToolTip
              v-if="item.summary"
              :title="item.summary"
              :auto-placement-options="{ allowedPlacements: ['bottom-start', 'top-start'] }"
            >
              {{ item.summary }}
            </ToolTip>
            <div
              v-else
              class="flex items-center gap-1 text-text-subtle"
            >
              <DotIndicator :reason="{ cause: 'computing' }" /> Analyzing…
            </div>
          </span>
        </template>

        <template #createdAt>
          <span class="capitalize">
            {{ useTimeAgo(item.createdAt) }}
          </span>
        </template>

        <template #actions>
          <div
            class="opacity-0 group-hover:opacity-100 focus-within:opacity-100"
            :class="{ 'opacity-100!': isMenuOpen }"
          >
            <DataRowMenu
              v-if="!isUploading"
              @menu-open="isMenuOpen = $event"
            />
          </div>
        </template>
      </RowGrid>
    </template>
  </FileTableRow>
</template>

<style scoped>
.selected:has(+ .selected) [data-selection-boundary] {
  border-bottom-left-radius: 0;
  border-bottom-right-radius: 0;
}

.selected + .selected [data-selection-boundary] {
  border-top-left-radius: 0;
  border-top-right-radius: 0;
}
</style>
